import React, { useLayoutEffect } from "react";
import type { PnlChartTagBarType, TimeRangeFinite } from "types/types";
import { PnlCategoriesEnum, PnlCategoryLabelsEnum } from "types/enums";
import { camelCase } from "lodash";
import { highlightTags as highlightTimelineTags } from "../../../pnlSummaryChartUtils";
import { useSelector } from "store/useSelector";
import { PnlColumnType } from "../pnlChartColumnsTypes";
import styled from "styled-components/macro";
import { FONT_SIZE_SMALLER } from "constants/constants";
import useContainerDimensions from "utils/useContainerDimensions";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";

const PNL_ROW_HEIGHT = 22;

export const DataRowCell = React.memo<{
  columnIdx: number;
  rowIdx: number;
  timeRange: TimeRangeFinite | null;
  isLeftmostColumnInChart: boolean;
  pnlChartWidth: number;
  bar: PnlChartTagBarType;
  pnlTitle?: PnlCategoryLabelsEnum | string;
  pnlColumn: PnlColumnType;
  pnlChartKey: number;
}>(
  ({
    columnIdx,
    rowIdx,
    timeRange,
    isLeftmostColumnInChart,
    pnlChartWidth,
    bar,
    pnlTitle,
    pnlColumn,
    pnlChartKey,
  }) => {
    const {
      title: tagTitle,
      category,
      // count,
      color,
      // time,
      // percent,
      // percentWidth
    } = bar;

    const dataDisplay = useDataDisplay(
      pnlColumn,
      category,
      tagTitle,
      bar,
      color,
      pnlTitle
    );

    const { translate } = useFlightTranslation();
    const dataDisplayTranslated =
      typeof dataDisplay === "string" ? translate(dataDisplay) : dataDisplay;

    const ref = useResizeHeightToMatchFirstColumn({
      rowIdx,
      category,
      skipResize: isLeftmostColumnInChart,
      pnlChartWidth,
      pnlChartKey,
      tagTitle,
    });

    const highlightBarTags = () => {
      highlightTimelineTags({
        tag: tagTitle,
        doHighlight: true,
        isOverall: false,
        rangeToHighlight: timeRange,
      });
    };
    const unhighlightBarTags = () =>
      highlightTimelineTags({
        tag: tagTitle,
        doHighlight: false,
        isOverall: false,
        rangeToHighlight: timeRange,
      });

    return (
      <DataRowCellStyles
        ref={ref}
        onMouseOver={highlightBarTags}
        onMouseOut={unhighlightBarTags}
        className={`barRow category-${category}${
          isLeftmostColumnInChart ? " tagTitle tag-" + rowIdx : ""
        } tag-${camelCase(tagTitle)} tag-${camelCase(category)} ${
          window.location.pathname.includes("fromSnapshots")
            ? "fromSnapshots"
            : ""
        }`}
      >
        {dataDisplayTranslated}
      </DataRowCellStyles>
    );
  }
);

/** data to display in the DataRowCell */
function useDataDisplay(
  pnlColumn: PnlColumnType,
  category: string,
  tagTitle: string,
  bar: PnlChartTagBarType,
  color: string,
  pnlTitle: string | undefined
) {
  const { outputLossUnit, dataKey, formatFn } = pnlColumn;

  const dataDumpsPnlLossesByUnit = useSelector(
    state => state.legacy.dataDumpsPnlLossesByUnit
  );

  const dataForCategory = outputLossUnit
    ? dataDumpsPnlLossesByUnit[outputLossUnit]?.pnlLosses[category]
    : null;

  const tags = dataForCategory?.tags;

  const outputLossForTag = tags?.find(tag => tag.name === tagTitle)?.outputLoss;

  // for columns displaying outputLoss by unit (e.g. cups, bottles), pull outputLoss for this unit & this tag
  const outputLossForUnit =
    // if it's a tag, try to find the tag's outputLoss
    outputLossForTag ||
    // if it's a non-"producing" category without tags e.g. Unknown > "Unknown Stop Time", try to use the loss sum for the category
    (!tags && category !== PnlCategoriesEnum.producing
      ? dataForCategory?.outputLoss
      : null);

  // for all other columns, show the data from the bar using dataKey
  const barData = outputLossUnit ? outputLossForUnit : bar[dataKey];

  return formatFn({
    [dataKey]: barData,
    color,
    pnlTitle: pnlTitle as PnlCategoryLabelsEnum,
  });
}

/** if a tag name is long, it causes the first column to grow vertically --
 * so, here we imperatively set all cells in each row to the height of the leftmost (tag name) cell
 */
function useResizeHeightToMatchFirstColumn({
  rowIdx,
  category,
  skipResize,
  pnlChartWidth,
  pnlChartKey,
  tagTitle,
}: {
  rowIdx: number;
  category: string;
  skipResize: boolean;
  pnlChartWidth: number;
  pnlChartKey: number;
  tagTitle: string;
}) {
  const [ref, dimensions] = useContainerDimensions();
  const windowSize = useWindowSize();

  // resize each cell to match its associated tag title (leftmost) cell
  useLayoutEffect(() => {
    if (!ref.current || skipResize) {
      return;
    }
    // set the element's height to match that of the first column in the same row
    const tagTitleCell = document.querySelector(
      `.pnlChartKey-${pnlChartKey} .category-${category}.tagTitle.tag-${rowIdx}`
    );
    if (!tagTitleCell) {
      return;
    }
    const tagTigleRect = tagTitleCell.getBoundingClientRect();
    const tagTitleHeight = tagTigleRect.height;
    ref.current.style.height = tagTitleHeight + "px";
  }, [
    pnlChartWidth,
    rowIdx,
    category,
    skipResize,
    dimensions.width,
    dimensions.height,
    windowSize.innerWidth,
    pnlChartKey,
    ref,
    tagTitle,
  ]);

  return ref;
}

const DataRowCellStyles = styled.div`
  padding: 3px 6px;
  min-height: ${PNL_ROW_HEIGHT}px;
  &.fromSnapshots {
    max-height: ${PNL_ROW_HEIGHT}px;
  }
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZE_SMALLER}px;
  letter-spacing: 0.8px;
`;
